import { FormRows } from '@acorns/web-components'
import ApolloClient from 'apollo-client'
import { FormikBag } from 'formik'
import { StringSchema, object, string } from 'yup'

import AutoCompleteAddress from 'components/AutocompleteAddress'
import { Address } from 'generated/types'
import { sanitizeAddress } from 'utils/address'
import { transformErrors } from 'utils/errors'
import { FlowRoute } from 'utils/FlowProvider/types'
import states from 'utils/states'
import { zipMask } from 'utils/text-masks/zip'
import { noSpecialCharacters } from 'utils/validation-schemas/helpers'

import { createAddressErrorModal, createAddressSuggestionModal } from './deliverabilityModals'
import VerifyAddress from './graphql/verify-address.query.gql'
import {
  AddressProps,
  AddressType,
  EnhancedSpendContactInfoProps,
  GqlProps,
  StateProps,
} from './types'

// Validation messages
const addressError = 'Please enter a valid address.'
const poBoxError = 'P.O. Box is not supported.'
const cityError = 'Please enter a valid city.'
const stateError = 'Please enter a valid state.'
const zipCodeError = 'Please enter a valid zip code.'

noSpecialCharacters()

const getAddressFieldSchema = (
  addressField: string,
  addressType: AddressType,
  alsoResidentialAddress: boolean,
): StringSchema => {
  if (addressType === AddressType.Residential || alsoResidentialAddress) {
    return string().test('po-box-text', poBoxError, value => {
      const poBoxRegEx = new RegExp(/p?.o?.\s?box/i)
      return !poBoxRegEx.test(value)
    })
  } else {
    return string()
  }
}

const getValidationSchema = (addressType: AddressType, alsoResidentialAddress: boolean) =>
  object().shape({
    // Validation schema for address fields are different for Residential Addreses (PO Boxes disallowed)
    address1: getAddressFieldSchema('address1', addressType, alsoResidentialAddress)
      .trim()
      .required(addressError)
      .excludeSpecialCharacters(),
    address2: getAddressFieldSchema(
      'address2',
      addressType,
      alsoResidentialAddress,
    ).excludeSpecialCharacters(),
    city: string()
      .trim()
      .required(cityError)
      .excludeSpecialCharacters(),
    state: string().required(stateError),
    zip: string()
      .trim()
      .required(zipCodeError)
      .min(5, zipCodeError)
      .max(5, zipCodeError),
  })

// Handles Formik's validate event.
export const validate = (values, props) => {
  let errors = {}

  if (props.submitAttempted || props.forGrayOutCTACheck) {
    try {
      getValidationSchema(props.addressType, values.alsoResidentialAddress).validateSync(values, {
        abortEarly: false,
      })
    } catch (validationErrors) {
      // Convert validation errors to Formik errors collection
      errors = transformErrors(validationErrors.inner)
    }
  }

  return errors
}

export const mapPropsToValues: (props: StateProps & GqlProps) => AddressProps = ({
  addressType,
  data,
}) => {
  const user = data.user

  // Transform data into a format that sanitizeAddress can handle
  // Look for appropriate address type
  const address: Address = data.addresses.find(
    addr => addr.addressType === (addressType === AddressType.Mailing ? 'mailing' : 'residential'),
  )

  if (address) {
    user.address1 = address.street1
    user.address2 = address.street2
    user.city = address.city
    user.state = address.state
    user.zip = address.zipCode
  }

  const values: AddressProps = sanitizeAddress(data)

  if (addressType === AddressType.Mailing) {
    values.alsoResidentialAddress = true
  }

  return values
}

export const getRows: (
  addressType: AddressType,
  validateForm,
  fromSpendConfirmation: boolean,
) => FormRows = (addressType, validateForm, fromSpendConfirmation) => {
  const formRows: FormRows = [
    [
      {
        label: addressType === AddressType.Mailing ? 'Shipping address *' : 'Residential address *',
        name: 'address1',
        type: AutoCompleteAddress,
        placeholder: '123 Oak Drive',
        autocomplete: 'off',
        validateForm,
      },
      {
        label: 'Address (Line 2)',
        name: 'address2',
        type: 'text',
        placeholder: '#456',
      },
    ],
    [
      {
        label: 'City *',
        name: 'city',
        type: 'text',
        placeholder: 'Los Angeles',
      },
      {
        label: 'State *',
        name: 'state',
        options: states,
        type: 'select',
      },
    ],
    [
      {
        label: 'Zip Code *',
        mask: zipMask,
        name: 'zip',
        type: 'text',
        placeholder: '90007',
        inputMode: 'numeric',
        guide: false,
      },
    ],
  ]

  if (addressType === AddressType.Mailing && !fromSpendConfirmation) {
    formRows.push([
      {
        label: 'This is also my residential address',
        name: 'alsoResidentialAddress',
        type: 'checkbox',
      },
    ])
  }

  return formRows
}

export const handleSubmit = (
  values: AddressProps,
  formikBag: FormikBag<EnhancedSpendContactInfoProps, AddressProps>,
) => {
  const props = formikBag.props
  const client: ApolloClient<any> = props.client

  // Before verifying address deliverability, determine the state abbreviation.
  // The USPS service requires this, otherwise the service will return a suggested
  // address with nothing different other than the state value.
  const stateAbbreviation = states.filter(state => state.value === values.state)[0].label

  client
    .query({
      query: VerifyAddress,
      variables: {
        street1: values.address1,
        street2: values.address2,
        city: values.city,
        state: stateAbbreviation,
        zip: values.zip,
        country: 'US',
      },
    })
    .then(({ data: { addressDeliverability } }) => {
      const { addressType, closeErrorModal, route, saveAddress, setAddressType } = props
      const { setSubmitting } = formikBag

      // Check __typename, and show error modals accordingly
      switch (addressDeliverability.__typename) {
        case 'AddressDeliverabilityVerificationError':
          props.showErrorModal(createAddressErrorModal(values, props, formikBag))
          break
        case 'AddressDeliverabilityCorrectedAddress':
          props.showErrorModal(
            createAddressSuggestionModal(values, props, formikBag, addressDeliverability),
          )
          break
        default:
          saveAddress(addressType, values)
            .then(() => {
              if (
                addressType === AddressType.Residential ||
                (addressType === AddressType.Mailing && values.alsoResidentialAddress)
              ) {
                route({ from: FlowRoute.ContactInfo, action: 'continue' })
                return
              } else {
                setSubmitting(false)
                setAddressType(AddressType.Residential)
                closeErrorModal()
              }
            })
            .catch(error => {
              setSubmitting(false)
              closeErrorModal()
            })
      }
    })
}
